// Header search island: suggestions while typing (debounced, 2+ characters) as an accessible
// combobox. Without JavaScript the box is a plain GET form to /search.

const API = "/api/method/softech_commerce.api.catalog.suggest";
const MIN_LENGTH = 2;
const DELAY = 250;

const isPath = (url) => typeof url === "string" && url.startsWith("/") && !url.startsWith("//");

export function initSearch(form) {
	const input = form.querySelector("input[name=q]");
	const box = form.querySelector("[role=listbox]");
	if (!input || !box) return;
	let timer = 0;
	let request = 0;
	let active = -1;

	const options = () => [...box.querySelectorAll("[role=option]")];
	const close = () => {
		box.hidden = true;
		input.setAttribute("aria-expanded", "false");
		input.removeAttribute("aria-activedescendant");
		active = -1;
	};
	const highlight = (index) => {
		const all = options();
		if (!all.length) return;
		active = (index + all.length) % all.length;
		all.forEach((option, i) => option.setAttribute("aria-selected", String(i === active)));
		input.setAttribute("aria-activedescendant", all[active].id);
		all[active].scrollIntoView({ block: "nearest" });
	};

	const option = (id, href, text, extra) => {
		const link = document.createElement("a");
		link.id = id;
		link.href = href;
		link.className = "search-suggest__option";
		link.setAttribute("role", "option");
		link.setAttribute("aria-selected", "false");
		link.tabIndex = -1;
		const label = document.createElement("span");
		label.textContent = text;
		link.append(label);
		if (extra) {
			const small = document.createElement("span");
			small.className = "search-suggest__price";
			small.textContent = extra;
			link.append(small);
		}
		return link;
	};
	const heading = (text) => {
		const h = document.createElement("div");
		h.className = "search-suggest__heading";
		h.setAttribute("role", "presentation");
		h.textContent = text;
		return h;
	};

	const render = (data, q) => {
		const items = [];
		let n = 0;
		const categories = (data.categories || []).filter((c) => isPath(c.url));
		const products = (data.products || []).filter((p) => isPath(p.url));
		if (categories.length) {
			items.push(heading(box.dataset.labelCategories));
			categories.forEach((c) => items.push(option(`suggest-${n++}`, c.url, c.title)));
		}
		if (products.length) {
			items.push(heading(box.dataset.labelProducts));
			products.forEach((p) =>
				items.push(option(`suggest-${n++}`, p.url, p.title, p.price_display))
			);
		}
		const all = new URL(form.action, window.location.href);
		all.searchParams.set("q", q);
		items.push(option(`suggest-${n++}`, all.pathname + all.search, box.dataset.labelAll));
		box.replaceChildren(...items);
		box.hidden = false;
		input.setAttribute("aria-expanded", "true");
		input.removeAttribute("aria-activedescendant");
		active = -1;
	};

	const load = async (q) => {
		const id = ++request;
		const query = new URLSearchParams({ q, lang: form.dataset.lang || "" });
		try {
			const response = await fetch(`${API}?${query}`, {
				headers: { Accept: "application/json" },
			});
			if (!response.ok || id !== request) return;
			const data = (await response.json()).message || {};
			if (input.value.trim() === q) render(data, q);
		} catch (e) {
			// suggestions are optional; the form still submits to /search
		}
	};

	input.addEventListener("input", () => {
		clearTimeout(timer);
		const q = input.value.trim();
		if (q.length < MIN_LENGTH) return close();
		timer = setTimeout(() => load(q), DELAY);
	});
	input.addEventListener("keydown", (event) => {
		if (box.hidden) return;
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			highlight(active + (event.key === "ArrowDown" ? 1 : -1));
		} else if (event.key === "Enter" && active >= 0) {
			event.preventDefault();
			window.location.href = options()[active].href;
		} else if (event.key === "Escape") {
			event.preventDefault(); // keep the typed text (type=search clears it on Escape)
			close();
		}
	});
	document.addEventListener("click", (event) => {
		if (!form.contains(event.target)) close();
	});
	form.addEventListener("focusout", (event) => {
		if (!form.contains(event.relatedTarget)) close();
	});
}
